import _ from 'underscore';
import myw from 'myWorld-base';
import L from 'leaflet';

myw.GeomRotateMode = myw.MapInteractionMode.extend('GeomRotateMode', {
    options: {
        offset: 100,
        rotation: 0
    },

    /**
     * Initialization. Doesn't enable the mode
     * @class Provides a RotationMarker to allow a geometry to be rotated.
     * @param {L.MapControl}  map               Map on which the mode will be enabled
     * @param {object}        [options]         *optional* for overwriting default options
     * @param {integer}       [options.offset]  Default offset of rotation handle in pixels
     * @constructs
     * @extends {MapInteractionMode}
     */
    initialize(map, options) {
        this._map = map;
        this.setOptions(options);

        this._enabled = false;
        this._rotating = false;
        this._rotationMarker = undefined;
    },

    /**
     * Enables geometry rotating mode with the currently set options
     */
    enable() {
        myw.trace('rotation', 3, 'enable');
        this._enabled = true;
        this._setContextMenuForGeomRotateMode();
        this.startRotating();
    },

    /**
     * Disables geometry rotating mode
     */
    disable() {
        myw.trace('rotation', 3, 'disable');
        this.stopRotating();
        this._map.contextmenu.removeAllItems();
        this._rotating = false;
        this._enabled = false;
        const mode = this._map.currentInteractionMode();
        mode.setEditOverlay(this._overlay, mode._isOverlayRotatable, mode._rotation);
    },

    /**
     * This RotationMarker will rotate to face map clicks.
     * @param  {MouseEvent} event Mouse click event
     */
    handleMapClick(event) {
        this.rotateToPointer(event);
        return true;
    },

    /**
     * @return {boolean} True if this mode is enabled
     */
    isEnabled() {
        return this._enabled;
    },

    /**
     * @return {boolean} True if a rotation marker is currently active
     */
    isRotating() {
        return this.isUsable() && this._rotating;
    },

    /**
     * Check if this GeomRotateMode has what it requires to rotate a geometry
     * @returns {boolean} True if this mode is able to create a rotation marker
     */
    isUsable() {
        if (!this._enabled) return false;
        if (!this._map) return false;
        if (this._geomType != 'Point') return false;
        if (!this._overlay) return false; //cannot turn on rotate mode when feature has failed to be inserted
        if (!this._overlay.getLatLng) return false;
        return true;
    },

    /**
     * Sets the geometry type to create/edit.
     * @param {string} geomType One of 'Point', 'LineString' or 'Polygon'
     */
    setGeomType(geomType) {
        this._geomType = geomType;
    },

    /**
     * Sets the overlay to use when rotating an existing geometry
     * @param {null|L.Marker|L.Polyline|L.Polygon} overlay Overlay/geometry to rotate
     */
    setEditOverlay(overlay) {
        this._overlay = overlay;

        if (!overlay) return;

        const geom = myw.geometry(overlay.toGeoJSON(this.options.precision).geometry);
        this._initialGeom = geom;

        if (this._geomType !== 'Point') {
            geom.removeDuplicates();
            const cleanOverlay = L.GeoJSON.geometryToLayer(geom);
            overlay.setLatLngs(cleanOverlay.getLatLngs());
        }
    },

    /**
     * Assign an operation to handle rotation changes within this mode
     * @param {Function} func   The operation to run when rotation changes
     */
    setRotationChangeHandler(func) {
        this._rotationChangeHandler = func;
    },

    /**
     * Adds a RotationMarker to manage rotating the geometry.
     * If the optional event is provide the RotationMaker will rotate to
     * face the event.
     * @param  {MouseEvent} event (optional) Mouse click event
     */
    startRotating(event) {
        if (!this.isUsable()) return;

        if (this._rotationMarker) this.removeRotationMarker();

        const geomCenter = this._overlay.getLatLng();
        const latlng =
            event && event.latlng
                ? event.latlng
                : this._calculateOffsetPosition(geomCenter, this.options.rotation);

        this.addRotationMarker(latlng, geomCenter);
        if (this._rotationChangeHandler)
            this._rotationMarker.setRotationChangeHandler(this._rotationChangeHandler);
        if (event && event.latlng) this._rotationMarker.setLatLng(event.latlng);
    },

    /**
     * Removes the existing RotationMarker.
     */
    stopRotating() {
        this.removeRotationMarker();
    },

    /**
     * Start rotating, or stop rotating if already active.
     * @param  {MouseEvent} event (optional) Mouse click event
     */
    toggleRotation(event) {
        if (!this._rotating) this.startRotating(event);
        else this.stopRotating();
    },

    /**
     * Add a new RotationMarker to the map.
     * Remove's the existing rotationMarker if one is present
     * @param {L.LatLng} latlng              Position of the RotationMarker's handle
     * @param {L.LatLng} centerOfRotation    Position of the RotationMarker's center of rotation
     */
    addRotationMarker(latlng, centerOfRotation) {
        if (this._rotationMarker) this.removeRotationMarker();

        this._rotationMarker = new myw.RotationMarker(latlng, centerOfRotation, {
            offset: this.options.offset
        });
        this._rotationMarker.addTo(this._map);
        this._rotating = true;
        this.setMapEventListeners(this._map, 'on');
    },

    /**
     * Remove existing RotationMarker.
     */
    removeRotationMarker() {
        if (this._rotationMarker) {
            this.setMapEventListeners(this._map, 'off');
            this._rotationMarker.remove();
            this._rotationMarker = undefined;
        }
        this._rotating = false;
    },

    /**
     * Enable or disable map Event listeners
     * The rotation engine listenst to these events while rotating to control rotation.
     * @param {Map}    map       The map being used we add the rotationMarkers to.
     * @param {string} onOrOff   'on' to enable, 'off' to disable
     */
    setMapEventListeners(map, onOrOff) {
        map[onOrOff]('editable:disable', this.removeRotationMarker);
        map[onOrOff]('editable:drag', this._rotationMarker.onEditableDrag);
        map[onOrOff]('zoomend', this._rotationMarker.onMapZoomEnd);
    },

    /**
     * Rotates RotationMarker to face the Mouse click event
     * Will call startRotating if we aren't already rotating.
     * @param  {MouseEvent} event (optional) Mouse click event
     */
    rotateToPointer(event) {
        if (!event || !event.latlng) return;

        if (!this.isRotating()) {
            this.startRotating(event);
        } else {
            const centerOfRotation = this._rotationMarker.getCenterOfRotation();
            const theta = centerOfRotation.bearingTo(event.latlng);
            const latlng = this._calculateOffsetPosition(centerOfRotation, theta);
            this._rotationMarker.setLatLng(latlng);
        }
    },

    /*
     * Ends the current map interaction mode.
     */
    _endGeomRotateMode() {
        this._map.endCurrentInteractionMode();
    },

    /*
     * Context menu provides option to end this rotation mode
     */
    _setContextMenuForGeomRotateMode() {
        const contextmenu = this._map.contextmenu;
        contextmenu.removeAllItems();

        const endGeomRotateMode = {
            text: myw.msg('GeomRotateMode', 'geomRotateMode'),
            icon: 'images/activeContextMenuItem.png',
            callback: this._endGeomRotateMode.bind(this)
        };

        contextmenu.addItem(endGeomRotateMode);
    },

    /**
     * calculates an offset position using an origin and direction
     * @param {L.LatLng} origin   Original LatLng
     * @param {float}    theta    Bearing in decimal degrees
     * @returns {L.LatLng}
     * @private
     */
    _calculateOffsetPosition(origin, theta) {
        const PtA = this._map.project(origin);
        const PtB = myw.Util.createPointByVector(PtA, this.options.offset, theta);
        return this._map.unproject(PtB);
    }
});

myw.RotationMarker = L.Marker.extend({
    options: {
        draggable: true,
        icon: L.icon({
            iconSize: L.point(20, 20),
            iconUrl: 'images/markers/rotation_handle.svg'
        }),
        offset: 50,
        lineStyle: { color: 'rgb(128,0,0)', weight: 2, opacity: 0.6, dashArray: '2,4' },
        zIndexOffset: 1000
    },

    /**
     * @class Provides RotationMarker when editing a rotatable geometry.
     * @param  {L.LatLng}   latlng            Position of the RotationMarker's handle
     * @param  {L.LatLng}   centerOfRotation  Position of the RotationMarker's center of rotation
     * @param  {object}   [options]
     * @param  {boolean}  [options.offset=50] Length of RotationMarker's handle in pixels.
     * @constructs
     * @extends {L.Marker}
     */
    initialize(latlng, centerOfRotation, options) {
        if (options) {
            _.extend(this.options.offset, options.offset);
        }

        L.Marker.prototype.initialize.call(this, latlng, options);
        this._offset = this.options.offset;
        this._lineStyle = this.options.lineStyle;
        this.setCenterOfRotation(centerOfRotation);
        this._rotation = this._centerOfRotation.bearingTo(this._latlng);

        _.bindAll(
            this,
            'getCenterOfRotation',
            'setCenterOfRotation',
            'getOffset',
            'setOffset',
            'setRotationChangeHandler',
            'update',
            'onAdd',
            'onDrag',
            'onDragEnd',
            'onRemove',
            'onEditableDrag',
            'onMapZoomEnd',
            '_reposition',
            '_setCurrentOffset',
            '_setEventListeners',
            '_updateRotationLine',
            '_updateRotation'
        );
    },

    /**
     * Get this marker's center of rotation
     * @return {L.LatLng}
     */
    getCenterOfRotation() {
        return this._centerOfRotation;
    },

    /**
     * Update RotationMarker to a new center of rotation.
     * @param {L.LatLng} latlng
     */
    setCenterOfRotation(latlng) {
        this._centerOfRotation = L.latLng(latlng);
        this.update();
    },

    /**
     * Updates rotationLine when RotationMarker is updated.
     */
    update() {
        L.Marker.prototype.update.call(this);
        this._updateRotationLine();
        this._updateRotation();
    },

    /**
     * Returns this RotationMarker's current offset value
     * @returns {float} distance in pixels
     */
    getOffset() {
        return this._offset;
    },

    /**
     * Assign a new offset to this rotationMarker
     * This will reposition the end of the rotationMarker
     * @param {float} offset  distance of offset in pixels
     */
    setOffset(offset) {
        this._offset = offset;
        this._reposition();
    },

    /**
     * Assign function to call when this marker's rotation changes.
     * @param {Function} func function to call when rotation changes.
     */
    setRotationChangeHandler(func) {
        this._rotationChangeHandler = func;
    },

    /**
     * Redraw this marker's rotation line
     * @private
     */
    _updateRotationLine() {
        if (this._rotationLine)
            this._rotationLine.setLatLngs([this._centerOfRotation, this._latlng]);
    },

    /**
     * Update this marker's rotation and trigger rotationChange
     * @private
     */
    _updateRotation() {
        this._rotation = this._centerOfRotation.bearingTo(this._latlng);
        if (this._rotationChangeHandler) this._rotationChangeHandler(this._rotation);
    },

    /**
     * Add rotationLine and listeners for this marker when it is added to a map.
     * @param {L.Map} map  the map this marker is being added to.
     */
    onAdd(map) {
        L.Marker.prototype.onAdd.call(this, map);
        this._setEventListeners('on');
        this._rotationLine = L.polyline([this._centerOfRotation, this._latlng], this._lineStyle);
        this._rotationLine.addTo(map);
    },

    /**
     * Remove rotationLine and listeners when this marker is removed from a map.
     * @param {L.Map} map  the map this marker is being removed from.
     */
    onRemove(map) {
        this._rotationLine.remove();
        this._setEventListeners('off');
        L.Marker.prototype.onRemove.call(this, map);
    },

    /**
     * Reposition rotationLine as this marker is dragged
     * @param {event} e  original event
     */
    onDrag(e) {
        this.update();
    },

    /**
     * Update this marker's rotation when drag ends.
     * @param {event} e original event
     */
    onDragEnd(e) {
        this.update();
    },

    /**
     * Update center of rotation, and reposition this marker when editable is dragged
     * @param {event} e original event
     */
    onEditableDrag(e) {
        if (!e.latlng) return;
        this._centerOfRotation = e.latlng;
        this._reposition();
    },

    /**
     * Reposition this marker when MapZoom changes.
     * @param {event} e original event
     */
    onMapZoomEnd(e) {
        this._reposition();
    },

    /**
     * Enable/disable this marker's listeners
     * @param {string} onOrOff
     * @private
     */
    _setEventListeners(onOrOff) {
        this[onOrOff]('drag', this.onDrag);
        this[onOrOff]('dragend', this.onDragEnd);
    },

    /**
     * Set this marker's offset according to it's current position
     * @private
     */
    _setCurrentOffset() {
        const PtA = this._map.project(this._centerOfRotation);
        const PtB = this._map.project(this._latlng);
        this._offset = PtA.distanceTo(PtB);
    },

    /**
     * Reposition this marker according to it's center of rotation, rotation, and offset
     * @private
     */
    _reposition() {
        const alpha = this._map.project(this._centerOfRotation);
        const beta = myw.Util.createPointByVector(alpha, this._offset, this._rotation);
        this.setLatLng(this._map.unproject(beta));
    }
});

export default myw.GeomRotateMode;
